import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import useSuccessionApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams, formatDate } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import api from '../../services/api';
import PersonCell from '../../components/ui/PersonCell';

const Container = styled.div``;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const DetailModal = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SubTitle = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const SectionLabel = styled.h4`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 12px;
`;

const CandidateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const CandidateName = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReadinessBadge = styled.span`
  background: #EFF6FF;
  color: #1D4ED8;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const PriorityBadge = styled.span`
  background: #F3F4F6;
  color: #374151;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  white-space: nowrap;
`;

const NoCandidateWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #DC2626;
`;

const AddCandidateCard = styled.div`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $status, theme }) => {
    if ($status === 'active') return `${theme.colors.auxiliar.success}1A`;
    if ($status === 'review_needed') return `${theme.colors.auxiliar.warning}1A`;
    if ($status === 'filled') return `${theme.colors.primary.main}1A`;
    if ($status === 'closed') return `${theme.colors.text.muted}1A`;
    return `${theme.colors.text.muted}1A`;
  }};
  color: ${({ $status, theme }) => {
    if ($status === 'active') return theme.colors.auxiliar.success;
    if ($status === 'review_needed') return '#B45309';
    if ($status === 'filled') return theme.colors.primary.dark;
    if ($status === 'closed') return theme.colors.text.muted;
    return theme.colors.text.muted;
  }};
`;

const ActionsLoadError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const ActionsToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
`;

const CandidateActionsPanel = styled.div`
  width: 100%;
  background: #fff;
  border: 1px dashed #E8ECF0;
  border-radius: 6px;
  padding: 10px;
  margin: 4px 0 8px;
`;

const CandidateActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px solid #F0F2F5;
  &:last-child { border-bottom: none; }
`;

const CandidateActionTitle = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
  border-top: 1px solid #E8ECF0;
  padding-top: 16px;
`;

function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      // EmployeeController yalnız firstName/lastName/fullName [like] bind ediyor —
      // `name[like]` no-op'tu (arama filtrelemiyordu). Bkz. feedback-360/config.
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

const employeeAsync = buildEmployeeAsync();

const EMPTY_CANDIDATE_FORM = {
  candidateEmployeeId: null,
  readinessLevel: null,
  priority: '',
  developmentActions: '',
  notes: '',
};

// Task 35 — durum geçiş aksiyonları SuccessionStateMachine.CanTransition ile koşullu:
// her satırda yalnız STATUS_TRANSITIONS[row.status]'ta izinli olanlar gösterilir.
// active→review_needed BİLİNÇLİ OLARAK yok (yalnız periyodik tarama tetikler).
const TRANSITION_ICON_TYPE = { active: 'approve', review_needed: 'approve', filled: 'fill', closed: 'close' };

export default function SuccessionPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  // Position drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);

  // Detail modal
  const [detailModal, setDetailModal] = useState({ open: false, position: null });
  const [detailLoading, setDetailLoading] = useState(false);

  // Add candidate form (inside detail modal)
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [candidateForm, setCandidateForm] = useState(EMPTY_CANDIDATE_FORM);
  const [savingCandidate, setSavingCandidate] = useState(false);

  // Remove candidate confirm
  const [removeCandidateConfirm, setRemoveCandidateConfirm] = useState({ open: false, id: null, name: '' });
  const [removingCandidate, setRemovingCandidate] = useState(false);

  // Delete position confirm
  const [deletePositionConfirm, setDeletePositionConfirm] = useState({ open: false, id: null, name: '' });
  const [deletingPosition, setDeletingPosition] = useState(false);

  // Task 35 — aday altı aksiyon alt-listesi (SuccessionCandidateAction)
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const [candidateActions, setCandidateActions] = useState({});
  const [actionForm, setActionForm] = useState({ title: '', targetDate: '' });
  const [savingAction, setSavingAction] = useState(false);

  const {
    fetching, data, pagination, loadFailed, columns: baseColumns, refresh,
    getById, createPosition, updatePosition, deletePosition,
    addCandidate, removeCandidate,
    readinessOptions, statusOptions, STATUS_TRANSITIONS, transitionPosition,
    listCandidateActions, addCandidateAction, updateCandidateAction, removeCandidateAction,
  } = useSuccessionApi(queryParams);

  const positionSchema = useMemo(() => [
    {
      name: 'title',
      label: t('performance.succession.positionTitle'),
      type: 'text',
      grid: 12,
      required: true,
    },
    {
      name: 'criticalityReason',
      label: t('performance.succession.criticalityReason'),
      type: 'textarea',
      grid: 12,
    },
    {
      name: 'incumbentEmployeeId',
      label: t('performance.succession.incumbentEmployeeId'),
      type: 'asyncSelect',
      onSearch: employeeAsync.onSearch,
      onGetById: employeeAsync.onGetById,
      grid: 12,
    },
    {
      name: 'riskNote',
      label: t('performance.succession.riskNote'),
      type: 'textarea',
      grid: 12,
    },
    {
      name: 'gapAlertEnabled',
      label: t('performance.succession.gapAlertEnabled'),
      type: 'checkbox',
      grid: 12,
    },
  ], [t]);

  const handleSavePosition = useCallback(async (formData) => {
    let success;
    if (editingPosition) {
      success = await updatePosition(editingPosition.id, formData);
    } else {
      success = await createPosition(formData);
    }
    if (success) setDrawerOpen(false);
    return success;
  }, [editingPosition, createPosition, updatePosition]);

  const handleOpenDetail = useCallback(async (row) => {
    setDetailModal({ open: true, position: row });
    setDetailLoading(true);
    setShowAddCandidate(false);
    setCandidateForm(EMPTY_CANDIDATE_FORM);
    setExpandedCandidateId(null);
    setCandidateActions({});
    const detail = await getById(row.id);
    if (detail) setDetailModal({ open: true, position: detail });
    setDetailLoading(false);
  }, [getById]);

  const handleToggleActions = useCallback(async (candidateId) => {
    if (expandedCandidateId === candidateId) {
      setExpandedCandidateId(null);
      return;
    }
    setExpandedCandidateId(candidateId);
    if (!candidateActions[candidateId]) {
      const actions = await listCandidateActions(candidateId);
      setCandidateActions((prev) => ({ ...prev, [candidateId]: actions }));
    }
  }, [expandedCandidateId, candidateActions, listCandidateActions]);

  const handleAddAction = useCallback(async (candidateId) => {
    if (!actionForm.title.trim()) return;
    setSavingAction(true);
    const success = await addCandidateAction(candidateId, {
      title: actionForm.title,
      targetDate: actionForm.targetDate || undefined,
      status: 'open',
    });
    if (success) {
      const actions = await listCandidateActions(candidateId);
      setCandidateActions((prev) => ({ ...prev, [candidateId]: actions }));
      setActionForm({ title: '', targetDate: '' });
    }
    setSavingAction(false);
  }, [actionForm, addCandidateAction, listCandidateActions]);

  const handleToggleActionDone = useCallback(async (candidateId, action) => {
    const success = await updateCandidateAction(action.id, {
      title: action.title,
      targetDate: action.targetDate || undefined,
      status: action.status === 'done' ? 'open' : 'done',
    });
    if (success) {
      const actions = await listCandidateActions(candidateId);
      setCandidateActions((prev) => ({ ...prev, [candidateId]: actions }));
    }
  }, [updateCandidateAction, listCandidateActions]);

  const handleRemoveAction = useCallback(async (candidateId, actionId) => {
    const success = await removeCandidateAction(actionId);
    if (success) {
      const actions = await listCandidateActions(candidateId);
      setCandidateActions((prev) => ({ ...prev, [candidateId]: actions }));
    }
  }, [removeCandidateAction, listCandidateActions]);

  const handleTransition = useCallback(async (row, status) => {
    const success = await transitionPosition(row.id, status);
    if (success && detailModal.position?.id === row.id) {
      const detail = await getById(row.id);
      if (detail) setDetailModal((m) => ({ ...m, position: detail }));
    }
  }, [transitionPosition, detailModal.position, getById]);

  const handleAddCandidateSave = useCallback(async () => {
    if (!detailModal.position) return;
    setSavingCandidate(true);
    const payload = {
      candidateEmployeeId: candidateForm.candidateEmployeeId,
      readinessLevel: candidateForm.readinessLevel,
      priority: candidateForm.priority ? Number(candidateForm.priority) : undefined,
      developmentActions: candidateForm.developmentActions || undefined,
      notes: candidateForm.notes || undefined,
    };
    const success = await addCandidate(detailModal.position.id, payload);
    if (success) {
      // Reload detail
      const detail = await getById(detailModal.position.id);
      if (detail) setDetailModal((m) => ({ ...m, position: detail }));
      refresh();
      setShowAddCandidate(false);
      setCandidateForm(EMPTY_CANDIDATE_FORM);
    }
    setSavingCandidate(false);
  }, [detailModal.position, candidateForm, addCandidate, getById, refresh]);

  const handleRemoveCandidate = useCallback(async () => {
    setRemovingCandidate(true);
    const success = await removeCandidate(removeCandidateConfirm.id);
    if (success) {
      const detail = await getById(detailModal.position.id);
      if (detail) setDetailModal((m) => ({ ...m, position: detail }));
      refresh();
      setRemoveCandidateConfirm({ open: false, id: null, name: '' });
    }
    setRemovingCandidate(false);
  }, [removeCandidateConfirm.id, removeCandidate, getById, detailModal.position, refresh]);

  const handleDeletePosition = useCallback(async () => {
    setDeletingPosition(true);
    const success = await deletePosition(deletePositionConfirm.id);
    if (success) {
      setDeletePositionConfirm({ open: false, id: null, name: '' });
    }
    setDeletingPosition(false);
  }, [deletePositionConfirm.id, deletePosition]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useCallback((row) => {
    const options = [
      { type: 'detail', label: t('performance.succession.candidates'), onClick: handleOpenDetail },
    ];
    const allowedTransitions = STATUS_TRANSITIONS[row.status] || [];
    if (canWrite('TD') && allowedTransitions.length > 0) {
      options.push({ type: 'divider' });
      allowedTransitions.forEach((target) => {
        const opt = statusOptions.find((o) => o.id === target);
        options.push({
          type: TRANSITION_ICON_TYPE[target] || 'approve',
          label: t('performance.succession.transitionTo', { status: opt ? opt.name : target }),
          onClick: () => handleTransition(row, target),
        });
      });
    }
    if (canWrite('TD')) {
      options.push(
        { type: 'divider' },
        {
          type: 'edit',
          label: t('performance.common.edit'),
          onClick: () => {
            setEditingPosition(row);
            setDrawerOpen(true);
          },
        },
        { type: 'divider' },
        {
          type: 'delete',
          label: t('performance.common.delete'),
          onClick: () => setDeletePositionConfirm({ open: true, id: row.id, name: row.title }),
        },
      );
    }
    return options;
  }, [t, canWrite, handleOpenDetail, statusOptions, STATUS_TRANSITIONS, handleTransition]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={actionOptions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const canSaveCandidate = candidateForm.candidateEmployeeId && candidateForm.readinessLevel;
  const candidates = detailModal.position?.candidates || [];

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingPosition(null); }}
        title={editingPosition ? t('performance.common.edit') : t('performance.succession.addPosition')}
        schema={positionSchema}
        initialValues={editingPosition}
        onSave={handleSavePosition}
      />

      <ConfirmDialog
        isOpen={deletePositionConfirm.open}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: deletePositionConfirm.name })}
        onConfirm={handleDeletePosition}
        onCancel={() => setDeletePositionConfirm({ open: false, id: null, name: '' })}
        loading={deletingPosition}
      />

      <ConfirmDialog
        isOpen={removeCandidateConfirm.open}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: removeCandidateConfirm.name })}
        onConfirm={handleRemoveCandidate}
        onCancel={() => setRemoveCandidateConfirm({ open: false, id: null, name: '' })}
        loading={removingCandidate}
      />

      {/* Detail Modal */}
      <Overlay $open={detailModal.open}>
        {detailModal.open && (
          <DetailModal>
            <ModalTitle>
              {detailModal.position?.title}
              {detailModal.position?.status && (
                <StatusBadge $status={detailModal.position.status} style={{ marginLeft: 10 }}>
                  {t(`performance.succession.status.${detailModal.position.status}`, { defaultValue: detailModal.position.status })}
                </StatusBadge>
              )}
            </ModalTitle>
            {detailModal.position?.riskNote && (
              <SubTitle>{detailModal.position.riskNote}</SubTitle>
            )}
            {detailModal.position?.criticalityReason && (
              <SubTitle>{t('performance.succession.criticalityReason')}: {detailModal.position.criticalityReason}</SubTitle>
            )}

            {canWrite('TD') && detailModal.position && (STATUS_TRANSITIONS[detailModal.position.status] || []).length > 0 && (
              <Actions style={{ justifyContent: 'flex-start', marginTop: 0, marginBottom: 16 }}>
                {(STATUS_TRANSITIONS[detailModal.position.status] || []).map((target) => {
                  const opt = statusOptions.find((o) => o.id === target);
                  return (
                    <Button
                      key={target}
                      size="sm"
                      variant="outline"
                      onClick={() => handleTransition(detailModal.position, target)}
                    >
                      {t('performance.succession.transitionTo', { status: opt ? opt.name : target })}
                    </Button>
                  );
                })}
              </Actions>
            )}

            {detailModal.position?.hasNoCandidate && (
              <NoCandidateWarning>
                <AlertTriangle size={16} />
                {t('performance.succession.noCandidate')}
              </NoCandidateWarning>
            )}

            <SectionLabel>{t('performance.succession.candidates')}</SectionLabel>

            {detailLoading ? (
              <p style={{ fontSize: 13, color: '#6B7280' }}>{t('common.loading')}</p>
            ) : candidates.length === 0 ? (
              <p style={{ fontSize: 13, color: '#6B7280' }}>{t('performance.succession.noCandidate')}</p>
            ) : (
              candidates.map((c) => (
                <div key={c.id}>
                  <CandidateRow>
                    <CandidateName>
                      <PersonCell
                        name={c.candidateName}
                        profilePicture={c.candidateProfilePicture}
                        size={24}
                      />
                    </CandidateName>
                    <ReadinessBadge>
                      {t(`performance.succession.readiness.${c.readinessLevel}`, { defaultValue: c.readinessLevel })}
                    </ReadinessBadge>
                    {c.priority != null && (
                      <PriorityBadge>#{c.priority}</PriorityBadge>
                    )}
                    <ActionsToggle type="button" onClick={() => handleToggleActions(c.id)}>
                      {t('performance.succession.actions')} ({(candidateActions[c.id] || []).length || 0})
                    </ActionsToggle>
                    {canWrite('TD') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRemoveCandidateConfirm({ open: true, id: c.id, name: c.candidateName })}
                      >
                        {t('performance.common.delete')}
                      </Button>
                    )}
                  </CandidateRow>

                  {expandedCandidateId === c.id && (
                    <CandidateActionsPanel>
                      {/* TD-23 — null = OKUMA BAŞARISIZ (boş liste DEĞİL). İkisi aynı
                          metne düşerse kullanıcı, var olan aksiyonları "yok" sanır. */}
                      {candidateActions[c.id] === null ? (
                        <ActionsLoadError role="alert">{t('talent.loadFailed')}</ActionsLoadError>
                      ) : (candidateActions[c.id] || []).length === 0 ? (
                        <div style={{ fontSize: 12, color: '#6B7280' }}>{t('performance.succession.noActions')}</div>
                      ) : (
                        candidateActions[c.id].map((a) => (
                          <CandidateActionRow key={a.id}>
                            <CandidateActionTitle style={{ textDecoration: a.status === 'done' ? 'line-through' : 'none' }}>
                              {a.title}
                            </CandidateActionTitle>
                            {a.targetDate && <span>{formatDate(a.targetDate, i18n.language)}</span>}
                            {canWrite('TD') && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleToggleActionDone(c.id, a)}>
                                  {a.status === 'done' ? t('performance.succession.reopenAction') : t('performance.succession.completeAction')}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleRemoveAction(c.id, a.id)}>
                                  {t('common.delete')}
                                </Button>
                              </>
                            )}
                          </CandidateActionRow>
                        ))
                      )}
                      {canWrite('TD') && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
                          <div style={{ flex: 2 }}>
                            <TextInput
                              label={t('performance.succession.actionTitle')}
                              value={actionForm.title}
                              onChange={(e) => setActionForm((f) => ({ ...f, title: e.target.value }))}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <TextInput
                              label={t('performance.succession.actionTargetDate')}
                              type="date"
                              value={actionForm.targetDate}
                              onChange={(e) => setActionForm((f) => ({ ...f, targetDate: e.target.value }))}
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleAddAction(c.id)}
                            disabled={!actionForm.title.trim() || savingAction}
                            loading={savingAction}
                          >
                            {t('common.add')}
                          </Button>
                        </div>
                      )}
                    </CandidateActionsPanel>
                  )}
                </div>
              ))
            )}

            {canWrite('TD') && !showAddCandidate && (
              <Button
                variant="outline"
                style={{ marginTop: 12 }}
                onClick={() => { setShowAddCandidate(true); setCandidateForm(EMPTY_CANDIDATE_FORM); }}
              >
                {t('performance.succession.addCandidate')}
              </Button>
            )}

            {showAddCandidate && (
              <AddCandidateCard>
                <FieldGroup>
                  <AsyncGenericSelect
                    label={t('performance.succession.candidate')}
                    value={candidateForm.candidateEmployeeId}
                    onChange={(v) => setCandidateForm((f) => ({ ...f, candidateEmployeeId: v }))}
                    onSearch={employeeAsync.onSearch}
                    onGetById={employeeAsync.onGetById}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  <GenericSelect
                    label={t('performance.succession.readinessLabel')}
                    value={candidateForm.readinessLevel}
                    onChange={(v) => setCandidateForm((f) => ({ ...f, readinessLevel: v }))}
                    options={readinessOptions}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.succession.priority')}
                    type="number"
                    value={candidateForm.priority}
                    onChange={(e) => setCandidateForm((f) => ({ ...f, priority: e.target.value }))}
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.succession.developmentActions')}
                    type="textarea"
                    rows={2}
                    value={candidateForm.developmentActions}
                    onChange={(e) => setCandidateForm((f) => ({ ...f, developmentActions: e.target.value }))}
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.succession.notes')}
                    type="textarea"
                    rows={2}
                    value={candidateForm.notes}
                    onChange={(e) => setCandidateForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </FieldGroup>
                <Actions>
                  <Button variant="outline" onClick={() => setShowAddCandidate(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleAddCandidateSave}
                    disabled={!canSaveCandidate || savingCandidate}
                    loading={savingCandidate}
                  >
                    {t('common.save')}
                  </Button>
                </Actions>
              </AddCandidateCard>
            )}

            <ModalActions>
              <Button variant="outline" onClick={() => setDetailModal({ open: false, position: null })}>
                {t('common.cancel')}
              </Button>
            </ModalActions>
          </DetailModal>
        )}
      </Overlay>

      {/* TD-23 — başarısız okuma boş tabloya DEĞİL, uyarıya düşer. */}
      {loadFailed && <LoadErrorBanner />}

      <DataTable
        title={t('performance.succession.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('TD')}
        onNewButtonClick={() => { setEditingPosition(null); setDrawerOpen(true); }}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}
